import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowBigUpDash, CircleAlert, Eye, EyeOff, LoaderCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config/env';
import { useAppStore } from '../stores/useAppStore';
import { describeSignInFailure, readLastUsername, saveLastUsername } from '../utils/loginHelpers';

// The sign-in card is the head of this shop's own till receipt (logo, name, branch)
// with the form printed below it. Nothing moves: the counter PCs are old, and
// staff come here to type, not to watch.

// The receipt's torn bottom edge: white teeth over the page colour.
const TORN_EDGE = {
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='7'%3E%3Cpath d='M0 0h14L7 7z' fill='%23fff'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'repeat-x',
  backgroundPosition: 'center top',
};

// A soft pool of light on the counter where the receipt lies. Painted once.
const COUNTER = {
  backgroundImage: 'radial-gradient(ellipse 55% 60% at 50% 45%, rgba(255, 255, 255, 0.07), transparent 70%)',
};

// The dashed line a receipt prints between its header and the rest.
const PERFORATION = {
  backgroundImage: 'repeating-linear-gradient(90deg, #c5ccd8 0 8px, transparent 8px 14px)',
};

// `input-lg` opts out of the app-wide compact inputs (12px text) in index.css.
const fieldClass = (invalid) =>
  `input-lg block w-full rounded-lg border bg-white px-4 text-[#172033] transition-colors focus:outline-none focus:ring-4 ${
    invalid
      ? 'border-[#d92d20] focus:border-[#d92d20] focus:ring-red-600/15'
      : 'border-[#7b8598] hover:border-[#5a6478] focus:border-blue-600 focus:ring-blue-600/20'
  }`;

const labelClass = 'mb-2 block text-[15px] font-medium leading-5 text-[#172033]';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const businessInfo = useAppStore((state) => state.businessInfo);
  const shopName = businessInfo?.BusinessName || config.appTitle;
  const branch = businessInfo?.BranchLocation?.trim();

  // Whoever signed in last on this device. With a name already filled in,
  // the cursor starts in Password.
  const [rememberedUsername] = useState(readLastUsername);
  const [username, setUsername] = useState(rememberedUsername);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [problem, setProblem] = useState(null); // { field?, title, hint? }
  const [submitting, setSubmitting] = useState(false);
  const [logoMissing, setLogoMissing] = useState(false);

  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  // Name the shop in the browser tab too; one person may have several shops open.
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `Sign in - ${shopName}`;
    return () => {
      document.title = previousTitle;
    };
  }, [shopName]);

  const trackCapsLock = (event) => {
    if (typeof event.getModifierState === 'function') {
      setCapsLockOn(event.getModifierState('CapsLock'));
    }
  };

  // Enter in Username moves to Password while that is still empty, so
  // "name, Enter, password, Enter" works without touching the mouse.
  const handleUsernameKeyDown = (event) => {
    trackCapsLock(event);
    if (event.key === 'Enter' && !password) {
      event.preventDefault();
      passwordRef.current?.focus();
    }
  };

  const handleUsernameChange = (event) => {
    setUsername(event.target.value);
    setProblem(null);
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    setProblem(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    // Trim the username: phone keyboards and autofill often append a space,
    // and "manager " doesn't match "manager". Usernames never contain spaces.
    // The password is sent as typed.
    const name = username.trim();
    if (!name) {
      setProblem({ field: 'username', title: 'Type your username.' });
      usernameRef.current?.focus();
      return;
    }
    if (!password) {
      setProblem({ field: 'password', title: 'Type your password.' });
      passwordRef.current?.focus();
      return;
    }

    setSubmitting(true);
    setProblem(null);
    const result = await login(name, password);

    if (result.success) {
      saveLastUsername(name);
      navigate('/', { replace: true });
      return;
    }

    setSubmitting(false);
    const failure = describeSignInFailure(result);
    setProblem(failure);
    if (failure.field === 'both') {
      // Selected, so typing again replaces the wrong password.
      passwordRef.current?.focus();
      passwordRef.current?.select();
    }
  };

  const usernameInvalid = problem?.field === 'username' || problem?.field === 'both';
  const passwordInvalid = problem?.field === 'password' || problem?.field === 'both';
  const passwordNotes = [capsLockOn && 'login-caps-lock', problem && 'login-problem']
    .filter(Boolean)
    .join(' ');

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#1f2b45] px-4 py-10" style={COUNTER}>
      <div className="w-full max-w-[400px]">
        <div className="rounded-t-[3px] bg-white px-6 pb-8 pt-8 sm:px-9 sm:pt-9">
          <header className="text-center">
            {!logoMissing && (
              <div className="mb-4 flex h-28 items-center justify-center">
                <img
                  src={config.appIcon}
                  alt=""
                  onError={() => setLogoMissing(true)}
                  className="max-h-full max-w-[240px] object-contain"
                />
              </div>
            )}
            <h1 className="text-balance text-[28px] font-bold leading-[34px] tracking-[-0.015em] text-[#172033]">
              {shopName}
            </h1>
            {branch && <p className="mt-1 text-base text-[#535d70]">{branch} branch</p>}
          </header>

          <div aria-hidden="true" className="-mx-6 my-7 h-0.5 sm:-mx-9" style={PERFORATION} />

          <form onSubmit={handleSubmit} noValidate aria-label="Sign in" aria-busy={submitting}>
            <div>
              <label htmlFor="login-username" className={labelClass}>
                Username
              </label>
              <input
                id="login-username"
                ref={usernameRef}
                type="text"
                name="username"
                value={username}
                onChange={handleUsernameChange}
                onKeyDown={handleUsernameKeyDown}
                onKeyUp={trackCapsLock}
                autoFocus={!rememberedUsername}
                readOnly={submitting}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint={password ? 'go' : 'next'}
                aria-required="true"
                aria-invalid={usernameInvalid || undefined}
                aria-describedby={problem ? 'login-problem' : undefined}
                className={fieldClass(usernameInvalid)}
              />
            </div>

            <div className="mt-5">
              <label htmlFor="login-password" className={labelClass}>
                Password
              </label>
              <div className="relative">
                {/* Shown as plain text the phone must not "fix" the password,
                    hence no autocapitalize/autocorrect/spellcheck. */}
                <input
                  id="login-password"
                  ref={passwordRef}
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={password}
                  onChange={handlePasswordChange}
                  onKeyDown={trackCapsLock}
                  onKeyUp={trackCapsLock}
                  autoFocus={!!rememberedUsername}
                  readOnly={submitting}
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="go"
                  aria-required="true"
                  aria-invalid={passwordInvalid || undefined}
                  aria-describedby={passwordNotes || undefined}
                  className={`${fieldClass(passwordInvalid)} pr-12 [&::-ms-reveal]:hidden`}
                />
                {/* Out of the tab order so username → password → Enter stays
                    the keyboard flow; Enter must submit, not toggle. Clicking it
                    leaves the cursor in the password box. */}
                <button
                  type="button"
                  tabIndex={-1}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setShowPassword((shown) => !shown)}
                  className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-lg text-[#535d70] hover:text-[#172033]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <Eye className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
              <div aria-live="polite">
                {capsLockOn && (
                  <p
                    id="login-caps-lock"
                    className="mt-2 flex items-center gap-2 text-[15px] font-medium leading-5 text-[#93370d]"
                  >
                    <ArrowBigUpDash className="h-5 w-5 shrink-0" aria-hidden="true" />
                    Caps Lock is on.
                  </p>
                )}
              </div>
            </div>

            {problem && (
              <div
                id="login-problem"
                role="alert"
                className="mt-5 flex gap-3 rounded-lg border border-[#fda29b] bg-[#fef3f2] px-4 py-3 text-[#b42318]"
              >
                <CircleAlert className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <div className="text-[15px] leading-[22px]">
                  <p className="font-semibold">{problem.title}</p>
                  {problem.hint && <p>{problem.hint}</p>}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-6 flex h-[52px] w-full items-center justify-center gap-2 rounded-lg bg-[#1f2b45] px-4 text-base font-semibold text-white transition-colors hover:bg-[#2d3b5c] active:bg-[#18223a] focus:outline-none focus-visible:ring-[3px] focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-wait disabled:hover:bg-[#1f2b45]"
            >
              {submitting ? (
                <>
                  <LoaderCircle className="h-5 w-5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        </div>
        <div aria-hidden="true" className="h-[7px]" style={TORN_EDGE} />
      </div>
    </main>
  );
};

export default Login;
